/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import appInsights from './utils/appInsights';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: process.env.REACT_APP_SIGNUP_SIGNIN_POLICY,
    },
    authorities: {
        signUpSignIn: {
            authority: process.env.REACT_APP_SIGNIN_AUTHORITY,
        },
        forgotPassword: {
            authority: process.env.REACT_APP_FORGOT_PASSWORD_AUTHORITY,
        },
        editProfile: {
            authority: process.env.REACT_APP_EDIT_PROFILE_AUTHORITY,
        },
    },
    authorityDomain: process.env.REACT_APP_AUTHORITY_DOMAIN,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: process.env.REACT_APP_REDIRECT_URI, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI, // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        appInsights.trackException({
                            error: message,
                            severityLevel: SeverityLevel.Error
                        });
                        return;
                    case LogLevel.Info:
                        appInsights.trackException({
                            error: message,
                            severityLevel: SeverityLevel.info
                        });
                        return;
                    case LogLevel.Verbose:
                        appInsights.trackException({
                            error: message,
                            severityLevel: SeverityLevel.Verbose
                        });
                        return;
                    case LogLevel.Warning:
                        appInsights.trackException({
                            error: message,
                            severityLevel: SeverityLevel.Warning
                        });
                        return;
                    default:
                        return;
                }
            },
        },
    },
};


/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [],
};


/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
    scopes: process.env.REACT_APP_LOGIN_REQUEST_SCOPES.split(', ')
};